<template>
	<div class="group flex items-center text-left">
		<button
			v-if="!selectedStory"
			type="button"
			class="flex w-full justify-between px-md py-sm"
			@click="openShopPicker"
		>
			<div class="flex items-center">
				<v-icon icon="shop" class="mr-sm h-sm"></v-icon>

				<span class="text-sm tracking-xl">
					{{ t("choose-outlet") }}
				</span>
			</div>

			<v-icon icon="chevron-right" class="ml-auto text-xl"></v-icon>
		</button>

		<button
			v-else
			type="button"
			:aria-label="t('choose-outlet')"
			:title="t('choose-outlet')"
			class="flex w-full justify-between px-md py-sm underline-offset-2 hover:underline"
			@click="openShopPicker"
		>
			<template v-for="outlet in realOutlets" :key="outlet.outlet.id">
				<div
					v-if="outlet.outlet.title === selectedStory?.name"
					class="flex w-full flex-row items-start justify-start space-x-sm"
				>
					<div class="flex items-center space-x-3">
						<div
							v-if="outlet.availableQuantity >= 5"
							class="mt-[6px] ml-1 size-3 rounded-full bg-green-500"
						></div>

						<div
							v-else-if="
								outlet.availableQuantity >= 1 &&
								outlet.availableQuantity < 5 &&
								outlet.modelQuantity === 0
							"
							class="mt-[6px] ml-1 size-3 rounded-full bg-yellow-500"
						></div>

						<v-icon
							v-else-if="isBuyable && outlet.modelQuantity > 0"
							icon="tick-square"
							class="mt-1 fill-green-500 stroke-2 text-base text-white"
						></v-icon>

						<v-icon
							v-else
							icon="shop"
							class="mt-1 size-3 fill-blue-500 text-base text-white"
						></v-icon>
					</div>

					<div class="flex flex-col items-start">
						<p class="text-sm tracking-xl">
							{{ outlet.outlet.title }}
						</p>

						<!-- eslint-disable-next-line max-len -->
						<p
							v-if="
								outlet.availableQuantity > 0 &&
								outlet.availableQuantity < 5 &&
								outlet.modelQuantity > 0
							"
							class="font-sans text-sm tracking-xl text-gray-500"
						>
							{{ t("availability-few-left") }}
						</p>

						<p
							v-else-if="
								outlet.availableQuantity > 0 && outlet.modelQuantity > 0
							"
							class="font-sans text-sm tracking-xl text-gray-500"
						>
							{{ t("availability-buy-now") }}
						</p>

						<p
							v-else-if="
								outlet.availableQuantity > 0 && outlet.modelQuantity === 0
							"
							class="font-sans text-sm tracking-xl text-gray-500"
						>
							{{ t("availability-buy-now-no-model") }}
						</p>

						<p
							v-else-if="isBuyable && outlet.modelQuantity > 0"
							class="font-sans text-sm tracking-xl text-gray-500"
						>
							{{ t("model-order-from-store") }}
						</p>

						<p
							v-else-if="!isBuyable && outlet.modelQuantity > 0"
							class="font-sans text-sm tracking-xl text-gray-500"
						>
							{{ t("model-only") }}
						</p>

						<p
							v-else-if="isBuyable && outlet.modelQuantity === 0"
							class="font-sans text-sm tracking-xl text-gray-500"
						>
							{{ t("temporarily-out-of-stock") }}
						</p>

						<p v-else class="font-sans text-sm tracking-xl text-gray-500">
							{{ t("not-available") }}
						</p>
					</div>
				</div>
			</template>
		</button>
	</div>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "@storyblok/js";
import {useGtm} from "@gtm-support/vue-gtm";
import {useOutletStocks} from "@pkg/shared/composables/use-outlet-stocks";
import {useStoryblokOutlets} from "@pkg/shared/composables/use-storyblok-outlets";
import {useHydrated} from "@pkg/shared/composables/use-hydrated";
import {
	type SimpleProductDataFragment,
	type ViewProductDetailsQuery,
} from "@pkg/shared/graphql";

type ProductDetails = Extract<
	ViewProductDetailsQuery["product"],
	{__typename: "SimpleProduct"}
>;

const properties = defineProps<{
	product: SimpleProductDataFragment;
	productDetails?: ProductDetails;
}>();

const details = computed(() => properties.productDetails);

const {$eventbus} = useNuxtApp();
const {data: sbOutlets} = useStoryblokOutlets();

const hydrated = useHydrated();
const shopCookie = useCookie("selected-shop");

const selectedStory = computed<ISbStoryData | undefined>({
	get() {
		// Server-rendered pages may be cached and served to other visitors, so only show
		// the visitor's selected shop after hydration
		return hydrated.value && shopCookie.value
			? sbOutlets.value.find((outlet) => outlet.uuid === shopCookie.value)
			: undefined;
	},
	set(value) {
		shopCookie.value = value?.uuid ?? null;
	},
});

const gtm = useGtm();

function openShopPicker() {
	gtm?.trackEvent({
		event: "choose_shop",
		_clear: true,
	});
	$eventbus.emit("open-shop-picker");
}

const {t} = useI18n();

const isBuyable = computed(
	() => properties.product.inventory.isAvailableForPurchase ?? false,
);

const {outlets} = useOutletStocks(details);

// We have some outlets that aren't really shops but warehouses, just lazily filter them out
// Also see the product availability modal component
const whRegex = /varasto/i;
const realOutlets = computed(() =>
	outlets.value?.filter((outlet) => !whRegex.test(outlet.outlet.title)),
);
</script>

<!-- eslint-disable max-len -->
<i18n lang="json">
{
	"et": {
		"choose-outlet": "Valige kauplus",
		"availability-title": "Saadavus",
		"availability-model-content": "Kõiki tooteid saab osta igast kauplustest. Alati sama soodsad hinnad nii e-poes kui ka kauplustes!",
		"availability-model-info": "Paljud tooted saadavad erinevates värvides ning mudelites. Toote näidis võib kaupluste lõikes erineda.",
		"loading": "Laen kauplusi..",

		"availability-buy-now": "Kohe laos ja näidis olemas",
		"availability-few-left": "Veel viimased laos ja näidis olemas",
		"availability-buy-now-no-model": "Kohe kaupluse laost saadaval",
		"model-order-from-store": "Näidis olemas ja kauplusest tellitav",
		"model-only": "Näidis olemas",
		"temporarily-out-of-stock": "Kauplusest tellitav",
		"not-available": "Toode pole saadaval",
		"ask-from-store": "Küsi kauplusest!"
	},
	"fi": {
		"choose-outlet": "Valitse myymälä",
		"availability-title": "Saatavuus",
		"availability-model-content": "Kaikki tuotteet ostettavissa kaikista myymälöistä. Verkossa ja myymälöissä aina samat edulliset hinnat!",
		"availability-model-info": "Monista tuotteista on saatavilla lukuisia eri värejä ja malleja. Tuotteesta voi olla nähtäväillä eri versio myymälässäsi",
		"loading": "Ladataan myymälöitä..",

		"availability-buy-now": "Heti varastosta ja malli esillä",
		"availability-few-left": "Muutama jäljellä ja malli esillä",
		"availability-buy-now-no-model": "Heti myymälän varastosta",
		"model-order-from-store": "Malli esillä ja tilattavissa myymälästä",
		"model-only": "Malli esillä",
		"temporarily-out-of-stock": "Tilattavissa myymälästä",
		"not-available": "Ei saatavilla",
		"ask-from-store": "Kysy saatavuus myymälästä"
	}
}
</i18n>
